/** @prettier */
import React, {
    Component,
    FormEvent,
    useEffect,
    useState,
    useMemo,
    useCallback,
} from "react";
import "./QuestionEditor.css";
import {
    Button,
    ControlLabel,
    FormControl,
    FormGroup,
    HelpBlock,
    Label,
    FormControlProps,
} from "react-bootstrap";
import { saveQuestion } from "./questionApi";
import {
    DEFAULT_ANSWER_OPTIONS,
    QuestionAnswersEditor,
} from "./QuestionAnswers";
import QuestionTypePicker from "./QuestionTypePicker.js";
import References from "./References.js";
import ReinforcementQuestions from "./ReinforcementQuestions.js";
import ImageUploadGroup from "./ImageUploadGroup";
import { useRouteMatch, useHistory } from "react-router";
import { getStatusBsStyle, getStatusDisplayString } from "../util/statusUtils";
import { partitionImages } from "../util/imageUtils";
import { assert } from "../util/assert";
import { Subscription, Product, User } from "../services/qbankApi";
import {
    Subject,
    Category,
    Subcategory,
    QuestionImageData,
    RemovedImageData,
} from "./questionApi";
import * as questionApi from "./questionApi";
import { validateQuestion } from "./questionValidation";
import { QuestionViewer, QuestionViewerQuestionData } from "./QuestionViewer";
import { QuestionViewerModals } from "./QuestionViewerModals";

// A workaround for type issues with using FormControl's onChange directly
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/16208
const handleFormEvent = (onChange: (value: string) => void) => (
    event: FormEvent<FormControl & FormControlProps>,
) => {
    assert(typeof event.currentTarget.value === "string", "Expected a string");
    onChange(event.currentTarget.value);
};

class QuestionStem extends Component<{
    questionStem: string;
    setQuestionStem: (stem: string) => void;
    validationErrors: string;
}> {
    handleChange = handleFormEvent((stem) => this.props.setQuestionStem(stem));
    render() {
        return (
            <FormGroup className="question-text" controlId="question-text">
                <ControlLabel>Question stem</ControlLabel>
                <FormControl
                    componentClass="textarea"
                    value={this.props.questionStem}
                    rows={6}
                    onChange={this.handleChange}
                    placeholder={"Question stem"}
                ></FormControl>
                <HelpBlock className="validation-error has-error">
                    {this.props.validationErrors}
                </HelpBlock>
            </FormGroup>
        );
    }
}

class QuestionExplanation extends Component<{
    kind: "correct" | "incorrect";
    explanation: string;
    setExplanation: (val: string) => void;
    validationErrors: string;
}> {
    handleChange = handleFormEvent((val) => this.props.setExplanation(val));
    render() {
        return (
            <FormGroup
                controlId="question-explanation"
                className="explanation-container"
            >
                <ControlLabel>
                    {this.props.kind === "correct"
                        ? "Explanation of the correct answer"
                        : "Explanation of the incorrect answers"}
                </ControlLabel>
                <FormControl
                    value={this.props.explanation}
                    componentClass="textarea"
                    placeholder={
                        this.props.kind === "correct"
                            ? "Explain the correct answer."
                            : "Explain the incorrect answers."
                    }
                    onChange={this.handleChange}
                ></FormControl>
                <HelpBlock className="validation-error has-error">
                    {this.props.validationErrors}
                </HelpBlock>
            </FormGroup>
        );
    }
}

class LearningObjective extends Component<{
    learningObjective: string;
    setLearningObjective: (val: string) => void;
    validationErrors: string;
}> {
    handleChange = handleFormEvent((v) => this.props.setLearningObjective(v));

    render() {
        return (
            <FormGroup controlId="question-learning-objective">
                <ControlLabel>Learning objective</ControlLabel>
                {/* TODO: Check this FormControl for switching between managed and non-managed state */}
                <FormControl
                    type="text"
                    value={this.props.learningObjective}
                    placeholder="2-5 words"
                    onChange={this.handleChange}
                ></FormControl>
                <HelpBlock className="validation-error has-error">
                    {this.props.validationErrors}
                </HelpBlock>
            </FormGroup>
        );
    }
}

type KeyConceptProps = {
    keyConcept: string;
    setKeyConcept: (newVal: string) => void;
    validationErrors?: string;
};
const KeyConcept = ({
    keyConcept,
    setKeyConcept,
    validationErrors,
}: KeyConceptProps) => (
    <FormGroup controlId="question-learning-objective">
        <ControlLabel>Key concept</ControlLabel>
        <FormControl
            type="text"
            componentClass="textarea"
            value={keyConcept}
            placeholder="One or two sentence summary of the important concepts covered in this question"
            onChange={(e) => setKeyConcept((e.currentTarget as any).value)}
        ></FormControl>
        <HelpBlock className="validation-error has-error">
            {validationErrors ?? ""}
        </HelpBlock>
    </FormGroup>
);

type QuestionEditorProps = {
    qBankApi: typeof import("../services/qbankApi").default;
    me: User;
    activeSubscription: Subscription;
};

const initialQuestionData: questionApi.DraftQuestion = {
    uuid: undefined,
    product: undefined,
    subject: undefined,
    category: undefined,
    subcategory: undefined,
    status: "DRAFT",
    stem: "",
    correctAnswerExplanation: "",
    incorrectAnswerExplanation: "",
    learningObjective: "",
    keyConcept: "",
    references: [""],
    reinforcementQuestions: [],
    answerChoices: DEFAULT_ANSWER_OPTIONS,
    commentList: [],
};

const QuestionEditor = ({
    qBankApi,
    activeSubscription,
    me,
}: QuestionEditorProps) => {
    const match = useRouteMatch<{ questionId: string }>();
    const history = useHistory();
    const [productsList, setProductsList] = useState<Product[]>();
    const [subjectsList, setSubjectsList] = useState<Subject[]>();
    const [categoriesList, setCategoriesList] = useState<Category[]>();
    const [subcategoriesList, setSubcategoriesList] = useState<Subcategory[]>();

    const [hasChanges, setHasChanges] = useState(false);

    const [question, setQuestion] = useState<questionApi.DraftQuestion>(
        initialQuestionData,
    );

    const updateQuestion = useCallback(
        (updatedFields: Partial<questionApi.DraftQuestion>) => {
            setHasChanges(true);
            setQuestion((question) => ({ ...question, ...updatedFields }));
        },
        [],
    );

    const setSubject = (newSubject: Subject) => {
        updateQuestion({ subject: newSubject, category: undefined });
        qBankApi.getCategories(newSubject.uuid).then(setCategoriesList);
    };
    const setCategory = (newCategory: Category | undefined) => {
        // TODO: should clear subcategory?
        updateQuestion({ category: newCategory });

        newCategory &&
            qBankApi
                .getSubcategories(newCategory.uuid)
                .then(setSubcategoriesList);
    };
    const [questionStemImages, _setQuestionStemImages] = useState<
        QuestionImageData[]
    >([]);
    const setQuestionStemImages = (data: QuestionImageData[]) => {
        setHasChanges(true);
        _setQuestionStemImages(data);
    };
    const [questionExplanationImages, _setQuestionExplanationImages] = useState<
        QuestionImageData[]
    >([]);
    const setQuestionExplanationImages = (data: QuestionImageData[]) => {
        setHasChanges(true);
        _setQuestionExplanationImages(data);
    };
    const [removedImages, _setRemovedImages] = useState<RemovedImageData[]>([]);
    const setRemovedImages = (data: RemovedImageData[]) => {
        setHasChanges(true);
        _setRemovedImages(data);
    };

    const [showingErrors, setShowingErrors] = useState<"save" | "full">();
    const [showingPreview, setShowingPreview] = useState(false);
    const [modalState, setModalState] = useState<
        | "saveAsNewVersionDraft"
        | "saveSubmittedAsDraft"
        | "confirmDiscard"
        | "confirmDelete"
    >();

    // Might be "new", unlike question.uuid which will be a real id or undefined
    const questionIdParam = match?.params.questionId;

    useEffect(() => {
        // Reset state, if there was previous state
        setQuestion(initialQuestionData);
        setHasChanges(false);

        if (questionIdParam && questionIdParam !== "new") {
            const questionImagePromise =
                // Load image data from the API
                qBankApi
                    .getQuestionImageThumbnails(questionIdParam)
                    .catch(() => [])
                    .then((thumbnails) => {
                        let [
                            questionStemImages,
                            questionExplanationImages,
                        ] = partitionImages(thumbnails);
                        return {
                            questionStemImages,
                            questionExplanationImages,
                            removedImages: [],
                        };
                    });
            // Load the existing question
            questionApi.getQuestion(questionIdParam).then((question) => {
                return Promise.all([
                    qBankApi.getProducts(),
                    qBankApi.getSubjects(question.product.uuid),
                    qBankApi.getCategories(question.subject.uuid),
                    qBankApi.getSubcategories(question.category.uuid),
                    questionImagePromise,
                ]).then(
                    ([
                        productsList,
                        subjectsList,
                        categoriesList,
                        subcategoriesList,
                        imageData,
                    ]) => {
                        // Set list data
                        setProductsList(productsList);
                        setSubjectsList(subjectsList);
                        setCategoriesList(categoriesList);
                        setSubcategoriesList(subcategoriesList);

                        setQuestion(question);

                        // Set image data
                        const {
                            questionStemImages,
                            questionExplanationImages,
                            removedImages,
                        } = imageData;
                        // Use versions that don't trip the dirty flag
                        _setQuestionStemImages(questionStemImages);
                        _setQuestionExplanationImages(
                            questionExplanationImages,
                        );
                        _setRemovedImages(removedImages);
                    },
                );
            });
        } else {
            // It's a new question. Load the products
            qBankApi.getProducts().then((productsList) => {
                return qBankApi
                    .getSubjects(activeSubscription.product.uuid)
                    .then((subjectsResponse) => {
                        updateQuestion({ product: activeSubscription.product });
                        setProductsList(productsList);
                        setSubjectsList(subjectsResponse);
                    });
            });
        }
    }, [qBankApi, activeSubscription, questionIdParam, updateQuestion]);

    const validationErrors = useMemo(
        () =>
            validateQuestion(
                question,
                subjectsList,
                categoriesList,
                subcategoriesList,
            ),
        [question, subcategoriesList, subjectsList, categoriesList],
    );

    const saveValidationErrors = useMemo(
        () => ({
            subject: validationErrors.subject,
            category: validationErrors.category,
            subcategory: validationErrors.subcategory,
            learningObject: validationErrors.learningObject,
            reinforcementQuestions: validationErrors.reinforcementQuestions,
        }),
        [validationErrors],
    );

    const hasSaveValidationErrors = useMemo(
        () => Object.values(saveValidationErrors).some((err) => err.length),
        [saveValidationErrors],
    );

    const shownValidationErrors: Partial<typeof validationErrors> =
        showingErrors === "full"
            ? validationErrors
            : showingErrors === "save"
            ? saveValidationErrors
            : {};

    const handleSaveAsDraft = async () => {
        if (hasSaveValidationErrors) {
            setShowingErrors("save");
            return;
        }
        try {
            await saveQuestion(
                question.uuid,
                { ...question, status: "DRAFT" },
                {
                    removed: removedImages,
                    stem: questionStemImages,
                    explanation: questionExplanationImages,
                },
            );
            history.push("/questions");
        } catch (e) {
            console.error("Error saving question", e);
        }
    };
    const handlePreview = () => {
        if (!validationErrors.count) {
            setShowingPreview(true);
        } else {
            setShowingErrors("full");
        }
    };

    const previewQuestionData = useMemo(
        (): QuestionViewerQuestionData => ({
            ...question,
            // Assert these are defined:
            // we only preview when all the category data is filled out
            questionStemImages,
            questionExplanationImages,
            removedImages,
        }),
        [
            question,
            questionStemImages,
            questionExplanationImages,
            removedImages,
        ],
    );

    if (!productsList) {
        return (
            <div className="loading">
                <span>Loading</span>
            </div>
        );
    }

    const onDiscardChanges = () => {
        history.goBack();
    };

    const showErrorsClass = showingErrors ? "show-errors" : "";
    const questionClassNames = `question ${showErrorsClass}`;

    // Is a "V2" question, was previously published, created a new version
    const hasPreviousVersion = (question.versionInfo?.versionNum ?? 1) > 1;

    if (showingPreview) {
        return (
            <QuestionViewer
                question={previewQuestionData}
                isPreview={true}
                hasChanges={hasChanges}
                me={me}
                activeSubscription={activeSubscription}
                onEdit={() => setShowingPreview(false)}
            />
        );
    }

    const { subject, category, subcategory, status } = question;

    return (
        <div className={questionClassNames}>
            <Label
                className="question-status-label"
                bsStyle={getStatusBsStyle(status)}
            >
                {getStatusDisplayString(status)}
            </Label>
            {question.product && subjectsList && (
                <QuestionTypePicker
                    questionTypes={subjectsList}
                    typeName="Subject"
                    title={subject?.name ?? "Subject"}
                    setSelected={setSubject}
                />
            )}
            {!!shownValidationErrors.subject && (
                <div className="validation-error">
                    {shownValidationErrors.subject}
                </div>
            )}

            {subject && categoriesList && (
                <QuestionTypePicker
                    questionTypes={categoriesList}
                    typeName="Category"
                    title={category?.name ?? "Category"}
                    setSelected={setCategory}
                />
            )}
            {!!shownValidationErrors.category && (
                <div className="validation-error">
                    {shownValidationErrors.category}
                </div>
            )}

            {category && subcategoriesList && (
                <QuestionTypePicker
                    questionTypes={subcategoriesList}
                    typeName="Subcategory"
                    title={subcategory ? subcategory.name : "Subcategory"}
                    setSelected={(subcategory: Subcategory) =>
                        updateQuestion({ subcategory })
                    }
                />
            )}
            {!!shownValidationErrors.subcategory && (
                <div className="validation-error">
                    {shownValidationErrors.subcategory}
                </div>
            )}

            {subcategory && (
                <form
                    autoComplete="off"
                    className="question-form tab-container"
                    onSubmit={(e) => {
                        e.preventDefault(); // Don't actually do HTTP form submission
                        if (status === "PUBLISHED") {
                            setModalState("saveAsNewVersionDraft");
                        } else if (status === "SUBMITTED") {
                            setModalState("saveSubmittedAsDraft");
                        } else {
                            handleSaveAsDraft();
                        }
                    }}
                >
                    {/* <Prompt
                        when={ isFormDirty && status === "DRAFT"}
                        message={ location =>
                            (location.pathname === "/questions/new/view") ? true : "Question will be discarded if not saved as draft. Are you sure you want to discard draft?"
                        }
                    /> */}
                    <div>
                        <LearningObjective
                            learningObjective={question.learningObjective}
                            setLearningObjective={(learningObjective) =>
                                updateQuestion({ learningObjective })
                            }
                            validationErrors={
                                shownValidationErrors.learningObject ?? ""
                            }
                        />
                        <QuestionStem
                            questionStem={question.stem}
                            setQuestionStem={(stem) => updateQuestion({ stem })}
                            validationErrors={
                                shownValidationErrors.questionStem ?? ""
                            }
                        />
                        <ImageUploadGroup
                            key="stem"
                            type="stem"
                            images={questionStemImages}
                            removedImages={removedImages}
                            setImages={setQuestionStemImages}
                            setRemovedImages={setRemovedImages}
                        />
                        <QuestionAnswersEditor
                            answerChoices={question.answerChoices}
                            setAnswerChoices={(answerChoices) =>
                                updateQuestion({ answerChoices })
                            }
                            validationErrors={
                                shownValidationErrors.answerChoices ?? ""
                            }
                        />
                        <KeyConcept
                            keyConcept={question.keyConcept}
                            setKeyConcept={(keyConcept) =>
                                updateQuestion({ keyConcept })
                            }
                            validationErrors={shownValidationErrors.keyConcept}
                        />
                        <QuestionExplanation
                            kind="correct"
                            explanation={question.correctAnswerExplanation}
                            setExplanation={(correctAnswerExplanation) =>
                                updateQuestion({ correctAnswerExplanation })
                            }
                            validationErrors={
                                shownValidationErrors.correctAnswerExplanation ??
                                ""
                            }
                        />
                        <QuestionExplanation
                            kind="incorrect"
                            explanation={question.incorrectAnswerExplanation}
                            setExplanation={(incorrectAnswerExplanation) =>
                                updateQuestion({ incorrectAnswerExplanation })
                            }
                            validationErrors={
                                shownValidationErrors.incorrectAnswerExplanation ??
                                ""
                            }
                        />
                        <ImageUploadGroup
                            key="explanation"
                            type="explanation"
                            images={questionExplanationImages}
                            removedImages={removedImages}
                            setImages={setQuestionExplanationImages}
                            setRemovedImages={setRemovedImages}
                        />
                        <ReinforcementQuestions
                            reinforcementQuestions={
                                question.reinforcementQuestions
                            }
                            setReinforcementQuestions={(
                                reinforcementQuestions: questionApi.ReinforcementQuestion[],
                            ) => updateQuestion({ reinforcementQuestions })}
                            validationErrors={
                                shownValidationErrors.reinforcementQuestions ??
                                ""
                            }
                        />
                        <References
                            references={question.references}
                            setReferences={(references: string[]) =>
                                updateQuestion({ references })
                            }
                            validationErrors={
                                shownValidationErrors.references ?? ""
                            }
                        />
                    </div>
                    <div className="question-action-buttons">
                        <Button
                            type="submit"
                            bsStyle={getStatusBsStyle("DRAFT")}
                            disabled={!hasChanges}
                            title={!hasChanges ? "No changes to save" : ""}
                        >
                            {status === "PUBLISHED"
                                ? "Save as new version Draft"
                                : "Save as Draft"}
                        </Button>
                        <Button
                            type="button"
                            bsStyle="info"
                            onClick={() => handlePreview()}
                        >
                            Preview for submission
                        </Button>
                        {status === "DRAFT" && !!question.uuid && (
                            <Button
                                type="button"
                                bsStyle="danger"
                                onClick={() => {
                                    setModalState("confirmDelete");
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            type="button"
                            bsStyle="link"
                            onClick={() => {
                                hasChanges
                                    ? setModalState("confirmDiscard")
                                    : // No changes, no need to cofirm, just leave
                                      onDiscardChanges();
                            }}
                        >
                            {question.uuid
                                ? hasChanges
                                    ? "Discard changes"
                                    : "Cancel editing"
                                : "Discard Draft"}
                        </Button>
                    </div>
                </form>
            )}
            {modalState && (
                <QuestionViewerModals
                    state={modalState}
                    isNew={!question.uuid}
                    isPublished={status === "PUBLISHED"}
                    hasPrevVersion={hasPreviousVersion}
                    onCancel={() => setModalState(undefined)}
                    onConfirm={
                        modalState === "confirmDiscard"
                            ? onDiscardChanges
                            : modalState === "saveAsNewVersionDraft"
                            ? handleSaveAsDraft
                            : modalState === "saveSubmittedAsDraft"
                            ? handleSaveAsDraft
                            : modalState === "confirmDelete"
                            ? () => {
                                  questionApi
                                      .deleteQuestion(question.uuid!)
                                      .then(() => history.push("/questions"));
                              }
                            : modalState // never, unless we've forgotten to handle a case
                    }
                />
            )}
        </div>
    );
};

export default QuestionEditor;
